'use client';

import { ExternalLink, Mail, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  detectType,
  formatDate,
  formatDateTime,
  truncate,
} from '@/lib/integrations/utils';

interface SmartCellProps {
  value: unknown;
  maxLength?: number;
  onExpandObject?: (value: unknown) => void;
}

export function SmartCell({
  value,
  maxLength = 100,
  onExpandObject,
}: SmartCellProps) {
  const type = detectType(value);

  switch (type) {
    case 'null':
      return <span className="text-muted-foreground">-</span>;

    case 'boolean':
      return (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Yes' : 'No'}
        </Badge>
      );

    case 'number':
      return <span className="font-mono">{(value as number).toLocaleString()}</span>;

    case 'url':
      return (
        <a
          href={value as string}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
          title={value as string}
        >
          {truncate(value as string, maxLength)}
          <ExternalLink className="size-3" />
        </a>
      );

    case 'email':
      return (
        <a
          href={`mailto:${value}`}
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
        >
          <Mail className="size-3" />
          {value as string}
        </a>
      );

    case 'date':
      return <span>{formatDate(value as string)}</span>;

    case 'datetime':
      return <span title={value as string}>{formatDateTime(value as string)}</span>;

    case 'array':
      const arr = value as unknown[];
      return (
        <div className="flex items-center gap-2">
          <Badge variant="outline">{arr.length} items</Badge>
          {onExpandObject && arr.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => onExpandObject(value)}
            >
              <Eye className="size-3" />
            </Button>
          )}
        </div>
      );

    case 'object':
      const obj = value as Record<string, unknown>;
      const keys = Object.keys(obj);
      return (
        <div className="flex items-center gap-2">
          <Badge variant="outline">{keys.length} fields</Badge>
          {onExpandObject && keys.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2"
              onClick={() => onExpandObject(value)}
            >
              <Eye className="size-3" />
            </Button>
          )}
        </div>
      );

    case 'string':
    default:
      const str = String(value);
      if (str.length > maxLength) {
        return <span title={str}>{truncate(str, maxLength)}</span>;
      }
      return <span>{str}</span>;
  }
}
